import React from 'react'
import PropTypes from 'prop-types'
import { mergeProps } from 'src/util'
import styles from './Grid.scss'

/**
 * A [CSS-Grid](#https://developer.mozilla.org/fr/docs/Web/CSS/CSS_Grid_Layout) component.
 */
const Grid = ({
  flow,
  autoColumns: gridAutoColumns,
  autoRows: gridAutoRows,
  columns: gridTemplateColumns,
  rows: gridTemplateRows,
  gap: gridGap,
  placeItems,
  alignItems,
  justifyItems,
  placeContent,
  alignContent,
  justifyContent,
  minHeight,
  scrollable,
  as: Wrapper,
  ...domProps
}) => (
  <Wrapper
    {...mergeProps()(
      {
        className: [styles.grid, { [styles.scrollable]: scrollable }],
        style: {
          gridAutoFlow:
            flow ||
            (gridTemplateColumns && gridTemplateRows ? undefined : gridTemplateColumns === true ? 'column' : 'row'),
          gridAutoColumns,
          gridAutoRows,
          gridTemplateColumns,
          gridTemplateRows,
          gridGap,
          alignItems: alignItems || (placeItems && placeItems.split(' ').shift()),
          justifyItems: justifyItems || (placeItems && placeItems.split(' ').pop()),
          alignContent: alignContent || (placeContent && placeContent.split(' ').shift()),
          justifyContent: justifyContent || (placeContent && placeContent.split(' ').pop()),
          minHeight,
        },
      },
      domProps
    )}
  />
)

Grid.defaultProps = {
  scrollable: false,
  as: 'div',
}

Grid.propTypes = {
  /**
   * CSS property `grid-template-columns`.
   */
  flow: PropTypes.oneOf(['row', 'column']),
  /**
   * CSS property `grid-auto-columns`.
   */
  autoColumns: PropTypes.string,
  /**
   * CSS property `grid-auto-rows`.
   */
  autoRows: PropTypes.string,
  /**
   * CSS property `grid-template-columns`.
   */
  columns: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  /**
   * CSS property `grid-template-rows`.
   */
  rows: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  /**
   * CSS property `grid-gap`.
   */
  gap: PropTypes.string,
  /**
   * CSS property `place-items` (shortcut for `align-items justify-items`).
   */
  placeItems: PropTypes.string,
  /**
   * CSS property `align-items`.
   */
  alignItems: PropTypes.string,
  /**
   * CSS property `justify-items`.
   */
  justifyItems: PropTypes.string,
  /**
   * CSS property `place-content` (shortcut for `align-content justify-content`).
   */
  placeContent: PropTypes.string,
  /**
   * CSS property `align-content`.
   */
  alignContent: PropTypes.string,
  /**
   * CSS property `justify-content`.
   */
  justifyContent: PropTypes.string,
  /**
   * CSS property `minHeight`.
   */
  minHeight: PropTypes.string,
  /**
   * Whether or not the grid container is supposed to be scrollable.
   * If not it automatically shrinks its children so that they fit inside their parent.
   */
  scrollable: PropTypes.bool,
  /**
   * The wrapper element of the grid.
   */
  as: PropTypes.elementType,
}

export default Grid
